import { LatLngType, Polygon } from "@repo/ping-react-js";

export const PARSE_TF_BLG_FOOTPRINT = (footprint: string) => {
  // Parse the JSON string into a JavaScript object
  if (!footprint) {
    return null;
  }
  const footprintObj = JSON.parse(footprint);
  const coordinates = footprintObj.coordinates[0];

  // @ts-ignore
  const mappedCoordinates = coordinates.map(([lng, lat]) => ({ lat, lng }));

  return mappedCoordinates;
};

export const PARSE_LINE_STRING = (linestr: string) => {
  // Remove "SRID=4326;LINESTRING (" at the beginning and ")" at the end
  const stripped = linestr
    .replace("SRID=4326;LINESTRING (", "")
    .replace(")", "");

  // Split the string by commas or spaces and filter out any empty parts
  const parts = stripped.split(/, | /).filter((part) => part);

  // Extract [lat, lng] arrays two elements at a time
  const coords: [number, number][] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const lng = Number(parts[i]);
    const lat = Number(parts[i + 1]);
    coords.push([lat, lng]);
  }

  return coords;
};

export const PARSE_TF_POLYGON = (polygonString: string) => {
  // Remove "POLYGON ((" and "))" from the string
  if (!polygonString) {
    return null;
  }

  const coordinateString = polygonString.replace(
    /^POLYGON \(\((.*)\)\)$/,
    "$1",
  );

  // Split the coordinate string into an array of coordinate pairs
  const coordinateArray = coordinateString.split(", ");

  // Split each coordinate pair into an array of longitude and latitude
  const coordinates = coordinateArray.map((coordinatePair) => {
    const [lng, lat] = coordinatePair.split(" ");
    return { lat: Number(lat), lng: Number(lng) };
  });

  return coordinates;
};

function parseMultipolygon(multipolygonStr: string): any {
  // Use a regex to find all instances of coordinate pairs
  const regex = /(-?\d+\.\d+)\s(-?\d+\.\d+)/g;
  let matches;
  const coordinates = [];

  // Loop over the string, finding matches and pushing their numeric forms
  while ((matches = regex.exec(multipolygonStr)) !== null) {
    // Convert string matches to numbers and add to coordinates array
    coordinates.push({
      lat: parseFloat(matches[2]),
      lng: parseFloat(matches[1]),
    });
  }

  return coordinates;
}

export const PARSE_LIGHTBOX_PARCEL_POLYGON = (
  str: string,
): Array<{ lat: number; lng: number }> => {
  return parseMultipolygon(str);
};

export const linestringsToGooglePolyline = (
  linestrings: string[],
): LatLngType[][] => {
  if (!linestrings) {
    return [];
  }

  return linestrings.map((l: string) => {
    const parsedLine = PARSE_LINE_STRING(l);
    return parsedLine ? parsedLine.map((d) => ({ lat: d[0], lng: d[1] })) : [];
  });
};

export const PARSE_LIGHTBOX_BUILDING_LOOKUP_PARCEL = (wktString: string) => {
  if (!wktString) return [];

  const isMultipolygon = wktString.startsWith("MULTIPOLYGON");
  const isPolygon = wktString.startsWith("POLYGON");

  if (!isMultipolygon && !isPolygon) return [];

  if (isMultipolygon) {
    const polygonsStr = wktString.replace("MULTIPOLYGON ", "").slice(2, -2);

    const polygons = polygonsStr.split(")), ((").map((poly: string) => {
      return parseCoordinateString(poly);
    });

    return polygons;
  } else if (isPolygon) {
    const polygonStr = wktString.replace("POLYGON ", "").slice(1, -1);
    return [parseCoordinateString(polygonStr)];
  } else {
    return [];
  }
};

const parseCoordinateString = (coordString: string) => {
  return coordString
    .replace(/[()]/g, "")
    .split(",")
    .map((pair: string) => {
      const [lngStr, latStr] = pair.trim().split(" ");
      const lng = parseFloat(lngStr);
      const lat = parseFloat(latStr);

      if (isNaN(lng) || isNaN(lat)) {
        console.error(`Invalid coordinate pair: ${pair}`);
      }

      return {
        lat,
        lng,
      };
    });
};

export const parsePossibleStringLatLng = (latLng: LatLngType): LatLngType | null => {
  if (isNaN(latLng?.lat) || isNaN(latLng?.lng)) return null;

  let lat = latLng.lat;
  let lng = latLng.lng;

  if (typeof lat === "string") {
    lat = parseFloat(lat);
    if (isNaN(lat)) return null;
  }

  if (typeof lng === "string") {
    lng = parseFloat(lng);
    if (isNaN(lng)) return null;
  }

  return {
    lat,
    lng,
  };
};

export function parseWKTPolygon(wktString: string): Polygon[] | null {
  if (!wktString) return null;

  // Normalize and strip optional SRID prefix if present
  const normalized = wktString
    .trim()
    .replace(/^SRID=\d+;/i, "")
    .trim();

  if (/^POLYGON\s*\(\(/i.test(normalized)) {
    const coordPart = normalized
      .replace(/^POLYGON\s*\(\(/i, "")
      .replace(/\)\)\s*$/i, "");

    // Split rings by "),(" allowing variable whitespace
    const rings = coordPart.split(/\)\s*,\s*\(/);

    const outerRing = parseRing(rings[0]);
    const holes = rings.slice(1).map(parseRing);

    return [
      {
        outer: outerRing,
        holes: holes,
      },
    ];
  } else if (/^MULTIPOLYGON\s*\(\(\(/i.test(normalized)) {
    const multiPolygonContent = normalized
      .replace(/^MULTIPOLYGON\s*\(\(\(/i, "")
      .replace(/\)\)\)\s*$/i, "");

    // Split polygons by ")),((" allowing variable whitespace
    const polygonStrings = multiPolygonContent.split(/\)\)\s*,\s*\(\(/);

    return polygonStrings.map((polygonString) => {
      // Split rings within each polygon by "),(" allowing variable whitespace
      const rings = polygonString.split(/\)\s*,\s*\(/);

      const outerRing = parseRing(rings[0]);
      const holes = rings.slice(1).map(parseRing);

      return {
        outer: outerRing,
        holes: holes,
      };
    });
  }

  return null;
}

export function parseRing(ringString: string): LatLngType[] {
  // Remove any stray parentheses and trim
  const cleaned = ringString.replace(/[()]/g, "").trim();
  if (!cleaned) return [];

  return cleaned
    .split(/\s*,\s*/) // split coordinate pairs on commas with optional whitespace
    .map((coordPair) => {
      const parts = coordPair.trim().split(/\s+/); // split by any whitespace
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      return {
        lat,
        lng,
      };
    })
    .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));
}
